import { Injectable, inject } from '@angular/core';
import { catchError, finalize, firstValueFrom, lastValueFrom, Observable, throwError } from 'rxjs';

import {
  TWO_FACTOR_REPOSITORY,
  TwoFactorRepository
} from '@app/domain/repositories/ptrab/two-factor/two-factor.repository.contract';
import { Phone } from '@app/entities/interfaces/ptrab/shared/phone.interface';
import {
  TwoFactorOperationCode,
  TwoFactorUserStatus,
  TwoFactorValidationResult,
  TwoFactorValidationStatus
} from '@app/entities/interfaces/ptrab/two-factor-authorization.interface';
import { Phones } from '@app/entities/models/ptrab/shared/phone.model';
import { TwoFactor } from '@app/entities/models/ptrab/two-factor/two-factor.model';
// eslint-disable-next-line @mercadona/clean-architecture-boundaries
import { CodeValidationComponent } from '@app/presentation/components/ptrab/modals/code-validation/code-validation.component';
// eslint-disable-next-line @mercadona/clean-architecture-boundaries
import { SecondFaContactComponent } from '@app/presentation/components/ptrab/modals/second-fa-contact/second-fa-contact.component';
// eslint-disable-next-line @mercadona/clean-architecture-boundaries
import { SecondFaPhoneSelectionComponent } from '@app/presentation/components/ptrab/modals/second-fa-phone-selection/second-fa-phone-selection.component';
import { AlertService, Logger, ModalManager } from '@app/services';
import { LoadingService } from '@app/services/loading/loading.service';
import { PtrabSessionManager } from '@app/services/ptrab-session.manager.service';
import { getMinutesFromSeconds } from '@app/shared/utils/utils';

import { MDialogConfig } from '@mercadona/components-musi/dialog';
import { MTranslateService } from '@mercadona/core/translate';

import { TwoFactorUseCases } from './two-factor.use-cases.contract';

@Injectable({
  providedIn: 'root'
})
export class TwoFactorUseCasesImpl implements TwoFactorUseCases {
  private readonly modalOptions: MDialogConfig = {
    closeOnBackdropClick: true,
    closeOnNavigation: true,
    width: '38rem'
  };
  private logger = new Logger('TwoFactorController');

  readonly #twoFactorRepository: TwoFactorRepository = inject(TWO_FACTOR_REPOSITORY);
  readonly #loadingService = inject(LoadingService);
  readonly #modalManager = inject(ModalManager);
  readonly #alertService = inject(AlertService);
  readonly #translateService = inject(MTranslateService);
  readonly #ptrabSessionManager = inject(PtrabSessionManager);

  getPhoneList(): Observable<Phones> {
    return this.#twoFactorRepository.getPhoneList();
  }

  sendValidationCodeToPhone(operation: TwoFactorOperationCode, phone: string): Observable<TwoFactor> {
    return this.#twoFactorRepository.sendValidationCodeToPhone(operation, phone);
  }

  validateCode(operation: TwoFactorOperationCode, code: string, phone?: string): Observable<string> {
    return this.#twoFactorRepository.validateCode(operation, code, phone);
  }

  getUserStatus() {
    return this.#twoFactorRepository.getUserStatus();
  }

  async validateOperation(operation: TwoFactorOperationCode | undefined, isPortugueseUser: boolean): Promise<string> {
    let hasSession = await this.#ptrabSessionManager.checkSession();
    let userPhoneList: Phone[];
    let modalOptions: MDialogConfig;

    if (!hasSession) {
      return Promise.reject(TwoFactorValidationStatus.ERROR);
    }

    const isUserBlocked = await this.assertUserBlocked();
    if (isUserBlocked) {
      return Promise.reject(TwoFactorValidationStatus.USER_BLOCKED);
    }

    try {
      const phoneResponse = await lastValueFrom(this.fetchPhoneList());

      userPhoneList = phoneResponse?.phones;
    } catch {
      return Promise.reject(TwoFactorValidationStatus.ERROR);
    }

    if (!userPhoneList.length) {
      return this.showContactCoordinatorModal();
    }

    modalOptions = {
      ...this.modalOptions,
      data: {
        phones: userPhoneList,
        isPortugueseUser
      }
    };

    const phoneId = await lastValueFrom(
      this.#modalManager.openModal(SecondFaPhoneSelectionComponent, modalOptions).afterClosed()
    );

    if (!phoneId) {
      return Promise.reject(TwoFactorValidationStatus.CANCELLED);
    } else if (phoneId === TwoFactorOperationCode.REQUEST_OTHER_PHONE) {
      return this.showContactCoordinatorModal();
    }

    hasSession = await this.#ptrabSessionManager.checkSession();
    if (!hasSession) {
      return Promise.reject(TwoFactorValidationStatus.ERROR);
    }

    modalOptions = {
      ...this.modalOptions,
      data: { phoneId, operation }
    };

    const result: TwoFactorValidationResult = await lastValueFrom(
      this.#modalManager.openModal(CodeValidationComponent, modalOptions).afterClosed()
    );

    return result.status === TwoFactorValidationStatus.SUCCESS
      ? Promise.resolve(result.hash as string)
      : Promise.reject(result.status);
  }

  async validatePtrabLogin(): Promise<string> {
    let userPhoneList: Phone[];
    let toastMessage: string;
    let modalOptions: MDialogConfig;

    try {
      const phoneResponse = await lastValueFrom(this.fetchPhoneList());

      userPhoneList = phoneResponse?.phones;
      toastMessage = phoneResponse?.message;
    } catch {
      return Promise.reject(TwoFactorValidationStatus.ERROR);
    }

    if (!userPhoneList.length) {
      return this.showContactCoordinatorModal();
    }

    modalOptions = {
      ...this.modalOptions,
      data: { phones: userPhoneList, toastMessage }
    };

    const phoneId = await lastValueFrom(
      this.#modalManager.openModal(SecondFaPhoneSelectionComponent, modalOptions).afterClosed()
    );

    if (!phoneId) {
      return Promise.reject(TwoFactorValidationStatus.CANCELLED);
    } else if (phoneId === TwoFactorOperationCode.REQUEST_OTHER_PHONE) {
      return this.showContactCoordinatorModal();
    }

    modalOptions = {
      ...this.modalOptions,
      data: { phoneId, operation: TwoFactorOperationCode.LOGIN_2FA }
    };

    const result: TwoFactorValidationResult = await lastValueFrom(
      this.#modalManager.openModal(CodeValidationComponent, modalOptions).afterClosed()
    );

    if (!result) {
      return Promise.reject(TwoFactorValidationStatus.CANCELLED);
    }

    return result.status === TwoFactorValidationStatus.SUCCESS
      ? Promise.resolve(result.hash as string)
      : Promise.reject(result.status);
  }

  async assertUserBlocked() {
    try {
      const userStatus = await firstValueFrom(this.#twoFactorRepository.getUserStatus());
      const isUserBlocked = userStatus?.status === TwoFactorUserStatus.BLOCKED;

      if (isUserBlocked) {
        this.showToastUserIsBlocked(userStatus?.remaining_time);
      }

      return isUserBlocked;
    } catch (error) {
      this.logger.error(error);
      return true;
    }
  }

  private async showContactCoordinatorModal(): Promise<TwoFactorOperationCode> {
    const modalOptions = {
      ...this.modalOptions
    };
    const dialogRef = this.#modalManager.openModal(SecondFaContactComponent, modalOptions);

    await lastValueFrom(dialogRef.afterClosed());

    return Promise.reject(TwoFactorOperationCode.REQUEST_OTHER_PHONE);
  }

  private showToastUserIsBlocked(remainingTime: number) {
    const title = this.#translateService.translate('TWO_FACTOR.EDITION_DISABLED');
    const description = this.#translateService.translate('TWO_FACTOR.TRIES_EXCEEDED.MESSAGE.MESSAGE', {
      minutes: getMinutesFromSeconds(remainingTime)
    });
    this.#alertService.showError(title, description);
  }

  private fetchPhoneList(): Observable<Phones> {
    this.#loadingService.show();
    return this.#twoFactorRepository.getPhoneList().pipe(
      finalize(() => {
        this.#loadingService.hide();
      }),
      catchError((error) => {
        this.#alertService.showError(
          this.#translateService.translate('UNEXPECTED.ERROR.TITLE'),
          this.#translateService.translate('UNEXPECTED.ERROR.SUBTITLE')
        );
        this.logger.error(error);
        return throwError(() => error);
      })
    );
  }
}
